import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useModules } from "@/lib/modules";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, ShoppingCart, Package, Users, FileText, Receipt,
  BarChart3, UserCog, CreditCard, Settings, X, ArrowRight, ArrowLeft, Check, Zap, Upload, Settings2, SkipForward,
} from "lucide-react";
import { askLabel } from "@/lib/helper";

// `feature` = backend Module Registry key that owns the step (module-aware tour, 2026-10-14). Steps without a key are core.
const STEPS = [
  { route: "/", icon: LayoutDashboard, title: "Your Dashboard", body: "This is your command center: today's sales, receivables, overdue balances and stock alerts at a glance." },
  { route: "/pos", icon: ShoppingCart, title: "POS Register", body: "Ring up sales fast. Take cash, card/WiPay or sell on credit. The register handles tax and receipts for you." },
  { route: "/inventory", icon: Package, title: "Products & Inventory", body: "Manage stock, prices and reorder levels. Import ready-made items from the Hardware Library." },
  { route: "/customers", icon: Users, title: "Customers & Credit", feature: "customers_credit", body: "Keep customer accounts, set credit limits and terms, and track exactly who owes you what." },
  { route: "/quotations", icon: FileText, title: "Quotations", feature: "quotations", body: "Create branded estimates and convert them to a sale in one click when the customer says yes." },
  { route: "/expenses", icon: Receipt, title: "Expenses", feature: "expenses", body: "Log business expenses so your profit reports reflect the real picture." },
  { route: "/reports", icon: BarChart3, title: "Reports", body: "Sales, profit, aging and top products, with date ranges and printable output." },
  { route: "/staff", icon: UserCog, title: "Employees", body: "Add managers and cashiers, invite them securely, and set approval PINs." },
  { route: "/payments-setup", icon: CreditCard, title: "Payments / WiPay", feature: "wipay", body: "Connect WiPay to accept card payments. Sandbox-test first, then complete the go-live checklist." },
  { route: "/settings", icon: Settings, title: "Settings", body: "Your branding, tax, modules and Security live here. You're all set. Enjoy NEEC POS!" },
];

// Fashion & Beauty wording (same tour engine, same completion flag). Hardware keeps STEPS unchanged.
const fashionSteps = (business) => [
  STEPS[0],
  { route: "/pos", icon: ShoppingCart, title: "POS Register", body: "Ring up sales fast. Tap a style, pick the size or colour in the variant picker, and take cash, card or customer credit. Use S / M / L on the toolbar to resize product pictures." },
  { route: "/inventory", icon: Package, title: "Products & Inventory", body: "Manage styles, sizes, colours and shades. Group variants under one parent product per department, with stock, prices and reorder levels for each." },
  STEPS[3],
  { route: "/import", icon: Upload, title: "Import Data & Pictures", body: "Bring in products, variants (Parent SKU + size / colour) and product pictures from a spreadsheet or a ZIP of photos named by SKU or barcode." },
  STEPS[5], STEPS[6], STEPS[7],
  { route: "/payments-setup", icon: CreditCard, title: "Payments", feature: "wipay", body: "Connect card payments when you're ready. Sandbox-test first, then complete the go-live checklist." },
  { route: "/settings", icon: Settings, title: "Settings", body: `Your branding, tax, departments, modules and Security live here. You're all set — and ${askLabel(business)} is one tap away whenever you need a hand. Enjoy NEEC POS!` },
];

// Module-aware copy. reason: null (available) | needs_activation | included_off | module_off (see lib/modules.js unavailableReason).
function stepView(step, modules) {
  const reason = step.feature ? modules.unavailableReason(step.feature) : null;
  const name = step.feature ? modules.moduleName(step.feature) : step.title;
  if (!reason) return { reason: null, body: step.body, navigate: true, nextLabel: "Next" };
  if (reason === "needs_activation")
    return { reason, navigate: true, nextLabel: "Next",
      body: `${name} is included in your Standard plan at no extra charge, but it is not activated yet. When your business is ready to offer customer accounts, activate it from Settings → Customers & Credit and choose Open Credit or Credit Line.` };
  if (reason === "included_off")
    return { reason, navigate: false, nextLabel: "Skip",
      body: `${name} is included in your Standard plan but is currently turned off for your business. Contact NEEC POS support to turn it on. You can skip this step for now.` };
  return { reason, navigate: false, nextLabel: "Skip",
    body: `${name} is an optional module that is not enabled for your business, so this step does not apply. Skip it for now — contact NEEC POS if you would like it added.` };
}

export default function GuidedTour() {
  const { tourOpen, closeTour, refreshUser, business } = useAuth();
  const modules = useModules();
  const nav = useNavigate();
  const [i, setI] = useState(0);
  const steps = business?.is_fashion ? fashionSteps(business) : STEPS;

  useEffect(() => { if (tourOpen) { setI(0); nav("/"); } }, [tourOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  // Never route the Owner into a module that is OFF: unavailable steps explain and stay on the current page.
  useEffect(() => { if (tourOpen && steps[i] && stepView(steps[i], modules).navigate) nav(steps[i].route); }, [i, tourOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!tourOpen) return null;
  const step = steps[i];
  const view = stepView(step, modules);
  const Icon = step.icon;
  const last = i === steps.length - 1;

  const finish = async (completed, to = "/") => {
    if (completed) { try { await api.post("/auth/complete-tour"); await refreshUser(); } catch {} }
    closeTour();
    nav(to);
  };

  return (
    <div className="fixed inset-0 z-[60] pointer-events-none" data-testid="guided-tour">
      <div className="absolute inset-0 bg-slate-950/40 pointer-events-auto" />
      <div className="absolute bottom-6 right-6 left-6 sm:left-auto sm:w-[380px] pointer-events-auto animate-fade-up">
        <div className="bg-card border border-border rounded-2xl shadow-2xl overflow-hidden" data-testid="tour-step" data-step-reason={view.reason || "available"}>
          <div className="bg-emerald-600 px-5 py-3 flex items-center justify-between text-white">
            <span className="text-xs font-semibold tracking-wider flex items-center gap-1.5"><Zap className="w-3.5 h-3.5" /> GETTING STARTED · {i + 1}/{steps.length}</span>
            <button onClick={() => finish(true)} className="opacity-80 hover:opacity-100" data-testid="tour-skip"><X className="w-4 h-4" /></button>
          </div>
          <div className="p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-11 h-11 rounded-xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center"><Icon className="w-6 h-6" /></div>
              <h3 className="font-head text-lg font-extrabold">{step.title}</h3>
            </div>
            {view.reason && (
              <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-600 mb-1" data-testid="tour-step-status">
                {view.reason === "needs_activation" ? "Included · activation required" : view.reason === "included_off" ? "Included · turned off" : "Optional module · not enabled"}
              </p>
            )}
            <p className="text-sm text-muted-foreground leading-relaxed" data-testid="tour-step-body">{view.body}</p>
            {view.reason === "needs_activation" && (
              <Button variant="outline" size="sm" className="mt-3" onClick={() => finish(true, "/settings?tab=customers")} data-testid="tour-setup-credit">
                <Settings2 className="w-4 h-4 mr-1" /> Finish tour &amp; set up Customer Credit
              </Button>
            )}
            <div className="flex items-center gap-1.5 mt-4">
              {steps.map((_, idx) => <div key={idx} className={`h-1.5 rounded-full flex-1 ${idx <= i ? "bg-emerald-500" : "bg-muted"}`} />)}
            </div>
            <div className="flex items-center justify-between mt-4">
              <Button variant="ghost" size="sm" disabled={i === 0} onClick={() => setI(i - 1)} data-testid="tour-back"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => finish(true)} data-testid="tour-skip-btn">Skip Tour</Button>
                {last
                  ? <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => finish(true)} data-testid="tour-finish"><Check className="w-4 h-4 mr-1" /> Finish</Button>
                  : <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setI(i + 1)} data-testid="tour-next">
                      {view.nextLabel === "Skip" ? <><SkipForward className="w-4 h-4 mr-1" /> Skip</> : <>Next <ArrowRight className="w-4 h-4 ml-1" /></>}
                    </Button>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
